import Flight, { FlightPassengerStops } from "./flight";
import { getRoutePointFromFlightPlanPoint, getRoutePointFromFlightPlanZZZZPoint } from "./flightPlan";
import { RoutePoint, getDistanceFromLatLonInNm } from "./route";

/**
 * Legs of a flight with intermediate stops.
 *
 * A flight has no leg entities: legs are implicit between consecutive points of
 * [departure, ...intermediateStops, destination] — the same index space the passengersLeg span
 * (EntityPassengersLegComponent) and passengerLegs use. IFR flights hide stops, so they are always
 * one leg even if stale stop data is still stored.
 */

export type FlightLegInfo = {
    index: number
    fromIndex: number
    toIndex: number
    from: RoutePoint
    to: RoutePoint
    distanceNM: number
    defaultEetMins: number //share of totalEETMins proportional to the leg distance
}

export function getFlightStopPoints(flight: Flight): RoutePoint[] {
    var departure: RoutePoint | undefined
    var destination: RoutePoint | undefined
    var stops: RoutePoint[] = []
    //same precedence as EntityDetailsPage's passengersLeg route: a custom route wins over a saved one
    if (flight.customRoute?.departure && flight.customRoute?.destination) {
        departure = getRoutePointFromFlightPlanPoint(flight.customRoute.departure)
        destination = getRoutePointFromFlightPlanPoint(flight.customRoute.destination)
        stops = flight.customRoute.intermediateStops ?? []
    } else if (flight.route) {
        departure = flight.route.departure
        destination = flight.route.destination
        stops = flight.route.intermediateStops ?? []
    }
    if (!departure || !destination) {
        return []
    }
    if (flight.flightRules !== "V") {
        stops = []
    }
    return [departure, ...stops, destination]
}

export function flightHasStops(flight: Flight): boolean {
    return getFlightStopPoints(flight).length > 2
}

//The whole routed polyline, used to measure each leg along the planned waypoints rather than as
//a straight line between stops.
function getRoutePolyline(flight: Flight): RoutePoint[] {
    if (flight.customRoute?.departure && flight.customRoute?.destination) {
        return [
            getRoutePointFromFlightPlanPoint(flight.customRoute.departure),
            ...flight.customRoute.waypoints.map((p) => getRoutePointFromFlightPlanZZZZPoint(p)),
            getRoutePointFromFlightPlanPoint(flight.customRoute.destination),
        ]
    }
    if (flight.route) {
        return [flight.route.departure, ...flight.route.waypoints, flight.route.destination]
    }
    return []
}

function sameRoutePoint(a: RoutePoint, b: RoutePoint): boolean {
    //stops are matched to waypoints by name in the route editors; coordinates break ties between
    //repeated names (e.g. two unnamed ZZZZ points)
    return a.name === b.name && Math.abs(a.lat - b.lat) < 0.01 && Math.abs(a.lon - b.lon) < 0.01
}

function polylineDistance(points: RoutePoint[]): number {
    var d = 0
    for (var i = 0; i < points.length - 1; i++) {
        d += getDistanceFromLatLonInNm(points[i].lat, points[i].lon, points[i + 1].lat, points[i + 1].lon)
    }
    return d
}

export function getFlightLegs(flight: Flight): FlightLegInfo[] {
    const stops = getFlightStopPoints(flight)
    if (stops.length < 2) {
        return []
    }

    //locate each stop on the polyline in order; a stop that can't be found falls back to a straight
    //line from the previous stop
    const polyline = getRoutePolyline(flight)
    const stopPolyIndex: (number | undefined)[] = []
    var searchFrom = 0
    stops.forEach((stop, i) => {
        if (i === 0) {
            stopPolyIndex.push(0)
            return
        }
        if (i === stops.length - 1) {
            stopPolyIndex.push(polyline.length - 1)
            return
        }
        const found = polyline.findIndex((p, pi) => pi >= searchFrom && sameRoutePoint(p, stop))
        stopPolyIndex.push(found === -1 ? undefined : found)
        if (found !== -1) searchFrom = found
    })

    const distances = stops.slice(0, -1).map((from, i) => {
        const a = stopPolyIndex[i], b = stopPolyIndex[i + 1]
        if (a !== undefined && b !== undefined && b > a) {
            return polylineDistance(polyline.slice(a, b + 1))
        }
        return polylineDistance([from, stops[i + 1]])
    })
    const totalDistance = distances.reduce((acc, d) => acc + d, 0)

    return distances.map((distanceNM, i) => ({
        index: i,
        fromIndex: i,
        toIndex: i + 1,
        from: stops[i],
        to: stops[i + 1],
        distanceNM,
        defaultEetMins: totalDistance > 0
            ? Math.max(5, Math.round((flight.totalEETMins ?? 0) * distanceNM / totalDistance))
            : Math.round((flight.totalEETMins ?? 0) / distances.length),
    }))
}

//The span a passenger is on board: their own passengerLegs entry, else the flight's passengersLeg
//span (the "Leg(s) with Passengers" the form already asks for), else the whole trip.
export function getPassengerStops(flight: Flight, passengerId: string | undefined): FlightPassengerStops {
    const lastIndex = Math.max(1, getFlightStopPoints(flight).length - 1)
    const span: FlightPassengerStops = flight.passengersLeg?.route
        ? { fromIndex: flight.passengersLeg.route.fromIndex, toIndex: flight.passengersLeg.route.toIndex }
        : { fromIndex: 0, toIndex: lastIndex }
    const own = passengerId ? flight.passengerLegs?.[passengerId] : undefined
    const stops = own ?? span
    return {
        fromIndex: Math.max(0, Math.min(stops.fromIndex, lastIndex - 1)),
        toIndex: Math.max(1, Math.min(stops.toIndex, lastIndex)),
    }
}

export function isPassengerOnLeg(flight: Flight, passengerId: string | undefined, legIndex: number): boolean {
    const stops = getPassengerStops(flight, passengerId)
    return stops.fromIndex <= legIndex && legIndex < stops.toIndex
}

//Passengers boarding / leaving at each stop index, from the per-passenger spans.
export function getPassengerChangesAtStops(flight: Flight): { embarking: number, disembarking: number }[] {
    const changes = getFlightStopPoints(flight).map(() => ({ embarking: 0, disembarking: 0 }))
    ;(flight.passengers ?? []).forEach((p) => {
        const stops = getPassengerStops(flight, p.id)
        if (changes[stops.fromIndex]) changes[stops.fromIndex].embarking++
        if (changes[stops.toIndex]) changes[stops.toIndex].disembarking++
    })
    return changes
}

//Estimated arrival at each stop point (index = stop index; departure = the departure time): route
//distance to the stop over the cruise speed, plus the standby at the stops before it, rounded up to
//5 minutes like Total EET. Used to pre-fill the boarding time printed on the boarding passes;
//undefined when the cruise speed or the route isn't known yet.
export function estimateStopArrivalTimes(flight: Flight, standbyMinsByStop: (number | undefined)[] = []): (Date | undefined)[] {
    const legs = getFlightLegs(flight)
    const departure = flight.departureDateTime ? new Date(flight.departureDateTime) : undefined
    const speed = flight.cruiseSpeed?.value
    if (!departure || isNaN(departure.getTime()) || !speed || legs.length === 0) {
        return legs.length ? [departure, ...legs.map(() => undefined)] : []
    }
    const speedKts = flight.cruiseSpeed.unit === "N" ? speed : speed * 0.539
    const out: (Date | undefined)[] = [departure]
    var minutes = 0
    legs.forEach((leg, i) => {
        if (i > 0) minutes += standbyMinsByStop[i] ?? 0 //standby at the stop this leg departs from
        minutes += leg.distanceNM / (speedKts / 60)
        out.push(new Date(departure.getTime() + Math.ceil(Math.round(minutes) / 5) * 5 * 60000))
    })
    return out
}
